import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useFetch } from '@/modules/core/composables/useFetch'
import type { ApplicationTemplate } from '../types'

export const useApplicationStore = defineStore('applications/applicationStore', () => {
  const route = useRoute()
  const baseUrl = computed(() => `/v4/${route.params.slug}/applications`)

  const templates = ref<ApplicationTemplate[]>([])
  const loading = ref(false)
  const error = ref(false)

  const fetchTemplates = async (): Promise<void> => {
    loading.value = true
    error.value = false
    try {
      templates.value = await useFetch(`${baseUrl.value}/templates`).get<ApplicationTemplate[]>()
    } catch {
      // keep the last known list; the page distinguishes error from empty
      error.value = true
    } finally {
      loading.value = false
    }
  }

  const createTemplate = async (name: string): Promise<ApplicationTemplate> => {
    const template = await useFetch(`${baseUrl.value}/templates`).post<ApplicationTemplate>({ name }, { silent: false })
    templates.value = [...templates.value, template]
    return template
  }

  const publishTemplate = async (id: number): Promise<void> => {
    await useFetch(`${baseUrl.value}/templates/${id}/publish`).post(undefined, { silent: false })
    templates.value = templates.value.map((t) => (t.id === id ? { ...t, state: 'published' } : t))
  }

  const draftTemplate = async (id: number): Promise<void> => {
    await useFetch(`${baseUrl.value}/templates/${id}/draft`).post(undefined, { silent: false })
    templates.value = templates.value.map((t) => (t.id === id ? { ...t, state: 'draft' } : t))
  }

  const archiveTemplate = async (id: number): Promise<void> => {
    await useFetch(`${baseUrl.value}/templates/${id}/archive`).post(undefined, { silent: false })
    templates.value = templates.value.map((t) => (t.id === id ? { ...t, state: 'archived' } : t))
  }

  const duplicateTemplate = async (id: number): Promise<ApplicationTemplate> => {
    const copy = await useFetch(`${baseUrl.value}/templates/${id}/duplicate`).post<ApplicationTemplate>(undefined, {
      silent: false,
    })
    templates.value = [...templates.value, copy]
    return copy
  }

  const deleteTemplate = async (id: number): Promise<void> => {
    await useFetch(`${baseUrl.value}/templates/${id}/destroy`).post(undefined, { silent: false })
    templates.value = templates.value.filter((t) => t.id !== id)
  }

  return {
    templates,
    loading,
    error,
    fetchTemplates,
    createTemplate,
    publishTemplate,
    draftTemplate,
    archiveTemplate,
    duplicateTemplate,
    deleteTemplate,
  }
})
